/**
 * WaveWrapUpPage — end-of-campaign optimization report.
 *
 * Renders a clean, black-on-white-friendly printable page that summarizes:
 *   • wave identity + timeline
 *   • total actions / extra spend / creators touched
 *   • per-lever rollup with counts, cost, creators touched
 *   • full chronological timeline of every lever pulled, with side-effects
 *
 * The user prints to PDF via their browser (Cmd/Ctrl-P). Dedicated print
 * stylesheet strips the sidebar/header chrome and uses black text on white
 * for page economy.
 *
 * Route: `/waves/:id/wrap-up`
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Printer, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function WaveWrapUpPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/brand/waves/${id}/optimization-log`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load wrap-up');
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (err) return <Shell><p className="text-red-600 text-sm" data-testid="wrap-up-error">{err}</p></Shell>;
  if (!data) return <Shell><Loader2 className="h-5 w-5 animate-spin text-[#00D4FF]" /></Shell>;

  const { wave = {}, brand_name, total_actions, total_extra_cost_usd, total_creators_touched, by_type = [], timeline = [] } = data;

  return (
    <Shell>
      <PrintStyles />
      <div className="max-w-[860px] mx-auto p-6 sm:p-10 text-gray-900 bg-white" data-testid="wave-wrap-up">
        {/* Top action bar — print-hidden */}
        <nav className="print:hidden flex items-center justify-between mb-6">
          <Link to={`/campaigns/${id}`} className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-black transition-colors" data-testid="wrap-up-back">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Control Room
          </Link>
          <button
            type="button"
            onClick={() => window.print()}
            data-testid="wrap-up-print"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-black text-white text-xs font-bold hover:bg-gray-800 transition-colors"
          >
            <Printer className="h-3.5 w-3.5" /> Print / Save PDF
          </button>
        </nav>

        {/* Masthead */}
        <header className="border-b-2 border-black pb-5 mb-6">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 font-bold">Wave Optimization Report</p>
          <h1 className="text-3xl font-black mt-1 leading-tight">{wave.name || 'Wave'}</h1>
          <p className="text-xs text-gray-600 mt-1">
            {brand_name} · {(wave.platforms || []).join(' · ') || 'all platforms'} · {wave.creator_count} creator{wave.creator_count === 1 ? '' : 's'}
            {wave.unique_hook ? <> · <span className="font-mono text-pink-700">{wave.unique_hook}</span></> : null}
          </p>
          <p className="text-[11px] text-gray-500 mt-1">
            Velocity band: <span className="font-semibold">{wave.velocity_band || '—'}</span> · Budget: <span className="font-semibold">${(wave.budget || 0).toLocaleString()}</span> · Status: <span className="font-semibold uppercase">{wave.status || '—'}</span>
          </p>
          <p className="text-[10px] text-gray-500 mt-2">
            Sent: {wave.sent_at ? new Date(wave.sent_at).toLocaleString() : '—'}
            {wave.paused_at && <> · Paused: {new Date(wave.paused_at).toLocaleString()}</>}
            {wave.resumed_at && <> · Resumed: {new Date(wave.resumed_at).toLocaleString()}</>}
          </p>
        </header>

        {/* Top-line numbers */}
        <section className="grid grid-cols-3 gap-4 mb-8" data-testid="wrap-up-totals">
          <Stat label="Total Optimizations" value={total_actions} />
          <Stat label="Extra Spend" value={`$${(total_extra_cost_usd || 0).toLocaleString()}`} />
          <Stat label="Creators Touched" value={total_creators_touched} />
        </section>

        {/* Per-type rollup */}
        {by_type.length > 0 && (
          <section className="mb-8" data-testid="wrap-up-by-type">
            <h2 className="text-sm font-black uppercase tracking-[0.15em] text-gray-600 mb-3">Optimizations by lever</h2>
            <table className="w-full text-[12px] border-collapse">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="text-left py-2 font-bold">Lever</th>
                  <th className="text-right py-2 font-bold">Count</th>
                  <th className="text-right py-2 font-bold">Cost</th>
                  <th className="text-right py-2 font-bold">Creators notified</th>
                </tr>
              </thead>
              <tbody>
                {by_type.map(r => (
                  <tr key={r.action_type} className="border-b border-gray-300">
                    <td className="py-2">{r.label}</td>
                    <td className="py-2 text-right tabular-nums">{r.count}</td>
                    <td className="py-2 text-right tabular-nums">{r.cost_usd ? `$${Number(r.cost_usd).toLocaleString()}` : '—'}</td>
                    <td className="py-2 text-right tabular-nums">{r.creators_touched}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Timeline */}
        <section data-testid="wrap-up-timeline">
          <h2 className="text-sm font-black uppercase tracking-[0.15em] text-gray-600 mb-3">Full chronological timeline</h2>
          {timeline.length === 0 ? (
            <p className="text-[12px] text-gray-500">No mid-flight optimizations were logged for this wave.</p>
          ) : (
            <ol className="relative space-y-3 pl-6 border-l-2 border-gray-300">
              {timeline.map(t => (
                <li key={t.action_id} className="relative" data-testid={`wrap-up-row-${t.action_id}`}>
                  <span className="absolute -left-[9px] top-1.5 w-3 h-3 rounded-full bg-pink-700 border-2 border-white" />
                  <p className="text-[12px] font-bold leading-snug">
                    {t.label}
                    <span className="font-normal text-gray-500 ml-2">· {t.created_at ? new Date(t.created_at).toLocaleString() : '—'}</span>
                  </p>
                  <p className="text-[11px] text-gray-700 leading-snug mt-0.5">{t.notes || '—'}</p>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-[10px] text-gray-600">
                    {t.cost_usd > 0 && <span>cost: <span className="font-semibold text-gray-900">${Number(t.cost_usd).toLocaleString()}</span></span>}
                    {t.notified_count > 0 && <span>notified: <span className="font-semibold text-gray-900">{t.notified_count}</span></span>}
                    {t.affected_count > 0 && <span>affected: <span className="font-semibold text-gray-900">{t.affected_count}</span></span>}
                    {t.side_effects?.new_angle && <span>new angle: <span className="font-semibold text-gray-900">{t.side_effects.new_angle}</span></span>}
                    {t.side_effects?.new_post_time && <span>new time: <span className="font-semibold text-gray-900">{new Date(t.side_effects.new_post_time).toLocaleString()}</span></span>}
                    {t.side_effects?.cta_text && <span>CTA: <span className="italic">"{t.side_effects.cta_text}"</span></span>}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>

        <footer className="mt-12 pt-5 border-t border-gray-300 text-[10px] text-gray-500">
          Generated {new Date().toLocaleString()} · WAVE Control Room · wave id {id}
        </footer>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-white text-gray-900 wave-wrap-shell">
      {children}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border-2 border-black p-4">
      <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">{label}</p>
      <p className="text-2xl font-black tabular-nums mt-1 leading-none">{value ?? '—'}</p>
    </div>
  );
}

// Print-friendly styles. The page already renders on white; these just strip
// margins and hide the app chrome when printing.
function PrintStyles() {
  return (
    <style>{`
      @media print {
        body, html, #root { background: #ffffff !important; }
        .wave-wrap-shell { background: #ffffff !important; }
        nav, aside, header[class*="border-b border-"] { display: none !important; }
        .print\\:hidden { display: none !important; }
        @page { margin: 0.6in; }
      }
    `}</style>
  );
}
